// frontend/src/pages/RegulatoryAlerts.jsx
// Module 5: Regulatory Intelligence — platform owner alert management page
// Shows detected source changes, manual review reminder for legislation.gov.au,
// and allows the platform owner to mark alerts as reviewed/actioned.
// commit: feat(w7): add RegulatoryAlerts platform owner page

import React, { useState, useEffect, useCallback } from 'react';
import {
  getRegulatoryAlerts,
  reviewRegulatoryAlert,
  getMonitorSources,
  markManualSourceReviewed,
  triggerMonitorRun,
} from '../api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function StatusBadge({ status }) {
  const map = {
    new:      { bg: '#fef3c7', color: '#92400e', label: 'New' },
    reviewed: { bg: '#d1fae5', color: '#065f46', label: 'Reviewed' },
    actioned: { bg: '#dbeafe', color: '#1e40af', label: 'Actioned' },
  };
  const s = map[status] || map.new;
  return (
    <span style={{
      display: 'inline-block',
      background: s.bg,
      color: s.color,
      borderRadius: 12,
      padding: '2px 10px',
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'capitalize',
    }}>
      {s.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Review modal
// ---------------------------------------------------------------------------
function ReviewModal({ alert, onClose, onSave }) {
  const [summary,  setSummary]  = useState(alert.summary || '');
  const [status,   setStatus]   = useState(alert.status === 'new' ? 'reviewed' : alert.status);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const data = await reviewRegulatoryAlert(alert.id, { summary, status });
      onSave(data.alert);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div style={{
        background: '#fff', borderRadius: 10, padding: 28, width: 480,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 16, color: '#132e50' }}>
          Review Alert
        </h2>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280' }}>
          {alert.source_name} — detected {formatDate(alert.detected_at)}
        </p>

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
          Status
        </label>
        <select
          value={status}
          onChange={e => setStatus(e.target.value)}
          style={{
            width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db',
            fontSize: 13, marginBottom: 14, background: '#f9fafb', color: '#374151',
          }}
        >
          <option value="reviewed">Reviewed — no clause change needed</option>
          <option value="actioned">Actioned — clause updated in Regulatory Library</option>
        </select>

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
          Summary note (optional)
        </label>
        <textarea
          value={summary}
          onChange={e => setSummary(e.target.value)}
          placeholder="Describe what changed and what action was taken…"
          rows={4}
          style={{
            width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db',
            fontSize: 13, resize: 'vertical', marginBottom: 16, boxSizing: 'border-box',
            fontFamily: 'inherit', color: '#374151',
          }}
        />

        {error && (
          <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 18px', borderRadius: 6, border: '1px solid #d1d5db',
              background: '#f9fafb', fontSize: 13, cursor: 'pointer', color: '#374151',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 18px', borderRadius: 6, border: 'none',
              background: '#132e50', color: '#fff', fontSize: 13,
              fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save Review'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function RegulatoryAlerts({ user }) {
  const [alerts,         setAlerts]         = useState([]);
  const [sources,        setSources]        = useState([]);
  const [loadingAlerts,  setLoadingAlerts]  = useState(true);
  const [loadingSources, setLoadingSources] = useState(true);
  const [selectedAlert,  setSelectedAlert]  = useState(null);
  const [runningCheck,   setRunningCheck]   = useState(false);
  const [runMessage,     setRunMessage]     = useState('');
  const [error,          setError]          = useState('');

  const load = useCallback(async () => {
    setLoadingAlerts(true);
    setLoadingSources(true);
    setError('');
    try {
      const [alertData, sourceData] = await Promise.all([
        getRegulatoryAlerts(),
        getMonitorSources(),
      ]);
      setAlerts(alertData?.alerts ?? []);
      setSources(sourceData?.sources ?? []);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoadingAlerts(false);
      setLoadingSources(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleManualReview(sourceId) {
    try {
      await markManualSourceReviewed(sourceId);
      setSources(prev =>
        prev.map(s => s.id === sourceId
          ? { ...s, manual_last_reviewed_at: new Date().toISOString() }
          : s
        )
      );
    } catch (err) {
      setError(err.message || 'Failed to update manual review');
    }
  }

  async function handleRunCheck() {
    setRunningCheck(true);
    setRunMessage('');
    try {
      const data = await triggerMonitorRun();
      setRunMessage(data.message || 'Check started');
      // Reload after 3 seconds to pick up any new alerts.
      setTimeout(load, 3000);
    } catch (err) {
      setRunMessage('Failed to start check: ' + err.message);
    } finally {
      setRunningCheck(false);
    }
  }

  function handleReviewSaved(updatedAlert) {
    setAlerts(prev => prev.map(a => a.id === updatedAlert.id ? updatedAlert : a));
    setSelectedAlert(null);
  }

  const newCount      = alerts.filter(a => a.status === 'new').length;
  const manualSources = sources.filter(s => s.is_manual);
  const autoSources   = sources.filter(s => !s.is_manual);

  return (
    <div style={{ padding: 32, maxWidth: 1100 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: '#132e50' }}>
            Regulatory Alerts
          </h1>
          <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
            Platform-level regulatory source monitoring. Review detected changes before updating clauses in the Regulatory Library.
          </p>
        </div>
        <button
          onClick={handleRunCheck}
          disabled={runningCheck}
          style={{
            padding: '8px 16px', borderRadius: 6, border: 'none',
            background: '#132e50', color: '#fff', fontSize: 13,
            fontWeight: 600, cursor: runningCheck ? 'not-allowed' : 'pointer',
            opacity: runningCheck ? 0.7 : 1, flexShrink: 0,
          }}
        >
          {runningCheck ? 'Running…' : 'Run Check Now'}
        </button>
      </div>

      {runMessage && (
        <div style={{ background: '#d1fae5', color: '#065f46', padding: '10px 14px', borderRadius: 6, fontSize: 13, marginBottom: 20 }}>
          {runMessage}
        </div>
      )}

      {error && (
        <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 6, fontSize: 13, marginBottom: 20 }}>
          {error}
        </div>
      )}

      {/* Manual source reminder cards */}
      {manualSources.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Manual Sources
          </h2>
          {manualSources.map(s => (
            <div key={s.id} style={{
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 8,
              padding: '14px 18px',
              marginBottom: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
            }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#132e50', marginBottom: 2 }}>
                  {s.source_name}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                  {s.notes}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                  Last manually reviewed: {s.manual_last_reviewed_at ? formatDate(s.manual_last_reviewed_at) : 'Never'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                <a
                  href={s.source_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    padding: '7px 14px', borderRadius: 6, border: '1px solid #d1d5db',
                    background: '#fff', color: '#374151', fontSize: 12,
                    fontWeight: 500, textDecoration: 'none', display: 'inline-block',
                  }}
                >
                  Open Source
                </a>
                <button
                  onClick={() => handleManualReview(s.id)}
                  style={{
                    padding: '7px 14px', borderRadius: 6, border: 'none',
                    background: '#132e50', color: '#fff', fontSize: 12,
                    fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Mark as Reviewed
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Automated source status */}
      {autoSources.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Automated Sources
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {autoSources.map(s => (
              <div key={s.id} style={{
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 8, padding: '14px 16px',
              }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#132e50', marginBottom: 4 }}>
                  {s.source_name}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 2 }}>
                  Last checked: {s.last_checked_at ? formatDate(s.last_checked_at) : 'Not yet run'}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                  Last changed: {s.last_changed_at ? formatDate(s.last_changed_at) : 'No change detected'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alerts table */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Detected Changes
          </h2>
          {newCount > 0 && (
            <span style={{
              background: '#fef3c7', color: '#92400e',
              fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
            }}>
              {newCount} unreviewed
            </span>
          )}
        </div>

        {loadingAlerts && (
          <div style={{ color: '#9ca3af', fontSize: 13, padding: 20 }}>Loading alerts…</div>
        )}

        {!loadingAlerts && alerts.length === 0 && (
          <div style={{
            background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
            padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 13,
          }}>
            No regulatory changes detected yet. Run a check or wait for the monthly cron.
          </div>
        )}

        {!loadingAlerts && alerts.length > 0 && (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['Source', 'Detected', 'Status', 'Reviewed By', 'Summary', ''].map((h, i) => (
                    <th key={i} style={{
                      padding: '10px 14px', textAlign: 'left',
                      fontWeight: 600, color: '#6b7280', fontSize: 11,
                      textTransform: 'uppercase', letterSpacing: 0.4,
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {alerts.map((a, i) => (
                  <tr key={a.id} style={{ borderBottom: i < alerts.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <td style={{ padding: '12px 14px', color: '#132e50', fontWeight: 500 }}>
                      <div>{a.source_name}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                        <a href={a.source_url} target="_blank" rel="noreferrer" style={{ color: '#9ca3af' }}>
                          {a.source_url.replace('https://', '').substring(0, 45)}…
                        </a>
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#374151' }}>
                      {formatDate(a.detected_at)}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <StatusBadge status={a.status} />
                    </td>
                    <td style={{ padding: '12px 14px', color: '#6b7280', fontSize: 12 }}>
                      {a.reviewed_by_name || '—'}
                      {a.reviewed_at && (
                        <div style={{ color: '#9ca3af', fontSize: 11 }}>{formatDate(a.reviewed_at)}</div>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#6b7280', fontSize: 12, maxWidth: 200 }}>
                      {a.summary || '—'}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {a.status === 'new' && (
                        <button
                          onClick={() => setSelectedAlert(a)}
                          style={{
                            padding: '5px 12px', borderRadius: 5, border: 'none',
                            background: '#132e50', color: '#fff', fontSize: 11,
                            fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          Review
                        </button>
                      )}
                      {a.status !== 'new' && (
                        <button
                          onClick={() => setSelectedAlert(a)}
                          style={{
                            padding: '5px 12px', borderRadius: 5,
                            border: '1px solid #d1d5db',
                            background: '#f9fafb', color: '#6b7280', fontSize: 11,
                            cursor: 'pointer',
                          }}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Review modal */}
      {selectedAlert && (
        <ReviewModal
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
          onSave={handleReviewSaved}
        />
      )}
    </div>
  );
}
